import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock, Trash2 } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { formatDate, calculateAge } from '@/utils/dateUtils';

export default function SearchPage() {
  const navigate = useNavigate();
  const { memories, children, tags } = useStore();
  const [query, setQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    const saved = localStorage.getItem('search-history');
    return saved ? JSON.parse(saved) : [];
  });

  // 搜索结果
  const results = useMemo(() => {
    if (!query.trim()) return [];
    
    const searchLower = query.toLowerCase();
    return memories.filter(memory => {
      const contentMatch = memory.content.toLowerCase().includes(searchLower);
      const tagMatch = tags.some(tag => 
        memory.tags.includes(tag.id) && 
        tag.name.toLowerCase().includes(searchLower)
      );
      const childMatch = children.some(child =>
        memory.childId === child.id &&
        child.name.toLowerCase().includes(searchLower)
      );
      return contentMatch || tagMatch || childMatch;
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [query, memories, tags, children]);

  // 执行搜索
  const handleSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    
    // 添加到历史
    const newHistory = [searchQuery, ...searchHistory.filter(h => h !== searchQuery)].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('search-history', JSON.stringify(newHistory));
  };

  // 清除历史
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('search-history');
  };

  // 删除单条历史
  const removeFromHistory = (item: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newHistory = searchHistory.filter(h => h !== item);
    setSearchHistory(newHistory);
    localStorage.setItem('search-history', JSON.stringify(newHistory));
  };

  return (
    <div className="min-h-screen bg-primary-50">
      {/* 搜索头部 */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 safe-top">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索内容、标签或孩子..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch(query)}
              className="w-full pl-10 pr-10 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
              autoFocus
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 font-medium"
          >
            取消
          </button>
        </div>
      </div>

      {/* 搜索结果或历史 */}
      <div className="px-4 py-4">
        {!query ? (
          // 搜索历史
          <>
            {searchHistory.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700">搜索历史</h3>
                  <button
                    onClick={clearHistory}
                    className="text-sm text-gray-400 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    清除
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {searchHistory.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(item)}
                      className="group flex items-center gap-1 px-3 py-1.5 bg-white rounded-full text-sm text-gray-600 card-shadow hover:bg-gray-50"
                    >
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {item}
                      <X 
                        className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100"
                        onClick={(e: React.MouseEvent) => removeFromHistory(item, e)}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* 热门标签 */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">热门标签</h3>
              <div className="flex flex-wrap gap-2">
                {tags.slice(0, 6).map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => handleSearch(tag.name)}
                    className="px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ 
                      backgroundColor: `${tag.color}20`,
                      color: tag.color 
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : results.length === 0 ? (
          // 无结果
          <div className="text-center py-16 text-gray-400">
            <Search className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>未找到相关记录</p>
            <p className="text-sm mt-1">换个关键词试试吧</p>
          </div>
        ) : (
          // 搜索结果
          <>
            <p className="text-sm text-gray-500 mb-4">
              找到 {results.length} 条相关记录
            </p>
            <div className="space-y-3">
              {results.map(memory => {
                const child = children.find(c => c.id === memory.childId);
                const memoryTags = tags.filter(t => memory.tags.includes(t.id));
                
                return (
                  <div
                    key={memory.id}
                    onClick={() => navigate(`/memory/${memory.id}`)}
                    className="bg-white rounded-xl p-4 card-shadow cursor-pointer active:scale-[0.98] transition-transform"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: child?.color || '#ccc' }}
                      >
                        {child?.name.charAt(0) || '?'}
                      </div>
                      <span className="text-sm font-medium">{child?.name}</span>
                      {child?.birthDate && child.type !== 'family' && (
                        <span className="text-xs text-gray-400">
                          {calculateAge(child.birthDate, memory.createdAt)}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">
                        {formatDate(memory.createdAt)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                      {memory.content}
                    </p>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      {memoryTags.map(tag => (
                        <span 
                          key={tag.id}
                          className="px-2 py-0.5 rounded-full text-xs"
                          style={{ 
                            backgroundColor: `${tag.color}20`,
                            color: tag.color 
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
